import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getRoleOrders, sortRoles } from "@/utils/roleUtils";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchExtendedWgData, updateExtendedWgData } from '@/api.ts';
import type { DayInfo, RoleInfo } from './roleInterfaces';
import type { HolidayInclusion, Schedule } from '@/types.ts';
import type { BusinessAccountExtendedWgItemData, GroupConstraints, SpecificNeedsConstraints } from '@/types/needsContrains';
import { NEED_KEY_LABELS, NEED_KEYS, NEEDS_INPUT_MODE_V2 } from './needKeys';
import RolesTableHeader from './RolesTableHeader';
import RolesTableRow from './RolesTableRow';
import NeedsExceptionCalendarModal from './NeedsExceptionCalendarModal';
import HolidayIndicatorRow from './HolidayIndicatorRow';
import Button from '@/components/button/Button';
import FilterButton from '@/components/button/FilterButton';
import TimelineFilterOverlay from '@/components/overlays/TimelineFilterOverlay';
import { format, max, min, parseISO } from 'date-fns';
import type { NeedsConstraintsExceptionsUpdateModel, UpdateModelForNeeds } from '@/types/needsContrainsRequest.ts';

interface RolesSettingsProps {
    schedule: Schedule | null;
    startDate: string | null;
    endDate: string | null;
}

// Legacy mode stores the holiday behaviour as per-day exception flags; the UI shows one
// value per role, so the most restrictive flag found on any day wins.
function inferHolidayInclusion(need: GroupConstraints | undefined): HolidayInclusion {
    const foundHolidayValues: { [k in HolidayInclusion]: boolean } = {
        Exclure: false,
        Inclure: false,
        Seulement: false
    };

    Object.values(need?.dicOfDaysOfWeek || {}).forEach((constraints) => {
        const holidayException = constraints?.exceptions?.holiday;
        if (holidayException && holidayException.active !== "false") {
            let hasSkip = false;
            if (holidayException.actionIfMatch === "skip") {
                foundHolidayValues.Exclure = true;
                hasSkip = true;
            }
            if (holidayException.actionIfNoMatch === "skip") {
                foundHolidayValues.Seulement = true;
                hasSkip = true;
            }
            if (!hasSkip) {
                foundHolidayValues.Inclure = true;
            }
        }
    });

    if (!(foundHolidayValues.Exclure || foundHolidayValues.Seulement)) {
        return "Inclure";
    }
    return foundHolidayValues.Exclure ? "Exclure" : "Seulement";
}


function getStartOfWeek(date: Date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
}

function getWeekDays(startDate: Date, holidays: ReadonlySet<string>) {
    const days: DayInfo[] = [];
    const dayNames = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

    for (let i = 0; i < 7; i++) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + i);
        const date = format(d, 'yyyy-MM-dd');
        days.push({
            name: dayNames[i] || "",
            date,
            displayDate: d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }),
            dayIndex: d.getDay().toString(),
            isHoliday: holidays.has(date)
        });
    }

    return days;
}

export default function RolesSettings({ schedule, startDate, endDate }: Readonly<RolesSettingsProps>) {
    const [updateToken, setUpdateToken] = useState<number>(1);
    const lastFetchedToken = useRef<number | null>(null);
    const knownRoleIds = useRef<Set<string>>(new Set());
    const [roles, setRoles] = useState<RoleInfo[]>([]);
    const [extendedData, setExtendedData] = useState<BusinessAccountExtendedWgItemData | null>(null);
    const [rolesMap, setRolesMap] = useState<{ [roleId: string]: RoleInfo }>({});
    const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<RoleInfo | null>(null);
    const [calendarRowKey, setCalendarRowKey] = useState<string | null>(null);
    const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
    const [roleFilterOpen, setRoleFilterOpen] = useState(false);
    // Roles whose extra need rows (holiday) are unfolded; collapsed by default.
    const [expandedNeedRoleIds, setExpandedNeedRoleIds] = useState<Set<string>>(new Set());
    const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
        if (startDate) {
            return getStartOfWeek(new Date(startDate + "T00:00:00"));
        }
        return getStartOfWeek(new Date());
    });

    useEffect(() => {
        if (lastFetchedToken.current === updateToken) {
            return;
        }

        lastFetchedToken.current = updateToken;

        fetchExtendedWgData().then((data: BusinessAccountExtendedWgItemData) => {
            setExtendedData(data);
        });
    }, [updateToken]);

    // Department setting carried by the extended data: one row block per dicOfNeeds key ("default" + "holiday").
    const useNewTaskNeedsUI = extendedData?.scheduleGenerationSettings?.needsContraintInputMode === NEEDS_INPUT_MODE_V2;

    useEffect(() => {
        if (!schedule) {
            return;
        }

        const data = extendedData;

        const uniqueRoles = new Map<string, string>();

        // A role nobody holds and with no shift yet still needs its row.
        schedule.roles?.forEach(role => {
            if (role.id && role.name && !role.outOfWg) {
                uniqueRoles.set(role.id, role.name);
            }
        });

        schedule.shifts.forEach(shift => {
            if (shift.roleId && shift.roleName && !shift.outOfWg) {
                uniqueRoles.set(shift.roleId, shift.roleName);
            }
        });

        schedule.employees.forEach(employee => {
            if ((employee.roles?.length || 0) > 0) {
                employee.roles?.forEach((role) => {
                    if (role.id && role.name && !role.outOfWg) {
                        uniqueRoles.set(role.id, role.name);
                    }
                })
            }
        });

        const mappedRoles: RoleInfo[] = [];
        const mappedRolesMap: { [roleId: string]: RoleInfo } = {};
        const defaultData = { active: "true", apply: "true", start: 0, end: 0, min: 0, max: 0 };
        const defaultConfig = {
            active: "true",
            apply: "true",
            dicOfDaysOfWeek: Object.fromEntries(
                ["1", "2", "3", "4", "5", "6", "0"].map(day => [day, { ...defaultData }])
            ),
            dicOfSpecificDays: {}
        };

        let temp_roles = [...uniqueRoles]
        const roleOrders = getRoleOrders(schedule);

        sortRoles(temp_roles, roleOrders, (r) => r[0], (r) => r[1]);

        temp_roles.forEach(([roleId, roleName]) => {
            const roleConstraints = data?.dicOfNeedsConstraintsTemplates?.["default"]?.dicOfRoles?.[roleId];

            const buildRoleInfo = (needId: string | undefined): RoleInfo => {
                const need = roleConstraints?.dicOfNeeds?.[needId ?? "default"];
                return {
                    roleId: roleId,
                    label: roleName,
                    needId,
                    rowKey: needId ? `${roleId}__${needId}` : roleId,
                    active: need?.active ?? "true",
                    apply: need?.apply ?? "true",
                    dicOfDaysOfWeek: need?.dicOfDaysOfWeek ?? defaultConfig.dicOfDaysOfWeek,
                    dicOfSpecificDays: need?.dicOfSpecificDays ?? defaultConfig.dicOfSpecificDays,
                    holiday: needId ? undefined : inferHolidayInclusion(need)
                };
            };

            const needIds: (string | undefined)[] = useNewTaskNeedsUI ? [...NEED_KEYS] : [undefined];
            needIds.forEach((needId) => {
                const info = buildRoleInfo(needId);
                mappedRoles.push(mappedRolesMap[info.rowKey] = info);
            });
        });

        setRoles(mappedRoles);
        setRolesMap(mappedRolesMap);

        const previouslyKnownRoleIds = knownRoleIds.current;
        knownRoleIds.current = new Set(mappedRoles.map(r => r.roleId));
        setSelectedRoleIds(prev => new Set(
            mappedRoles
                // A refetch must keep the user's choices; only roles never seen before default to visible.
                .filter(r => previouslyKnownRoleIds.has(r.roleId) ? prev.has(r.roleId) : true)
                .map(r => r.roleId)
        ));

    }, [schedule, extendedData, useNewTaskNeedsUI]);

    const holidaySet = useMemo(() => new Set(schedule?.holidays ?? []), [schedule?.holidays]);
    const weekDays = getWeekDays(currentWeekStart, holidaySet);

    const { periodStartDate, periodEndDate } = useMemo(() => {
        const shiftStarts = schedule?.shifts?.map(s => parseISO(s.start)) ?? [];
        const shiftEnds = schedule?.shifts?.map(s => parseISO(s.end)) ?? [];
        const today = new Date();
        const fallbackStart = shiftStarts.length ? min(shiftStarts) : today;
        const fallbackEnd = shiftEnds.length ? max(shiftEnds) : today;

        return {
            periodStartDate: startDate ?? format(fallbackStart, 'yyyy-MM-dd'),
            periodEndDate: endDate ?? format(fallbackEnd, 'yyyy-MM-dd'),
        };
    }, [schedule, startDate, endDate]);

    const roleFilterItems = useMemo(
        () => roles
            // Several rows may share one role (one per need key); the filter lists each role once.
            .filter((role, index) => roles.findIndex(r => r.roleId === role.roleId) === index)
            .map(role => ({ id: role.roleId, label: role.label })),
        [roles]
    );
    const visibleRoles = useMemo(
        () => roles.filter(role => selectedRoleIds.has(role.roleId)),
        [roles, selectedRoleIds]
    );
    const roleFilterCount = selectedRoleIds.size === roleFilterItems.length ? 0 : selectedRoleIds.size;
    // A holiday row is folded unless the user unfolded it or is editing it.
    const isFoldedHolidayRow = (role: RoleInfo) =>
        role.needId === NEED_KEYS[1] && !expandedNeedRoleIds.has(role.roleId) && editingRowKey !== role.rowKey;
    // The folded holiday row has no label link, so the calendar only cycles through unfolded rows.
    const calendarRoles = useMemo(
        () => visibleRoles.filter(role => !isFoldedHolidayRow(role)),
        [visibleRoles, expandedNeedRoleIds, editingRowKey]
    );

    const toggleNeedRows = (roleId: string) => {
        setExpandedNeedRoleIds(prev => {
            const next = new Set(prev);
            if (next.has(roleId)) {
                next.delete(roleId);
            } else {
                next.add(roleId);
            }
            return next;
        });
    };

    const calendarRoleIndex = calendarRowKey ? calendarRoles.findIndex(r => r.rowKey === calendarRowKey) : -1;
    const calendarRole = calendarRoleIndex >= 0 ? (calendarRoles[calendarRoleIndex] ?? null) : null;

    const handleEdit = (role: RoleInfo) => {
        setEditingRowKey(role.rowKey);
        setEditForm(JSON.parse(JSON.stringify(role)));
    };

    const handleCancel = () => {
        setEditingRowKey(null);
        setEditForm(null);
    };

    const handleSave = async () => {
        if (editForm) {
            const params = new URLSearchParams(window.location.search);
            const wgId = params.get("wgId") || "";
            const businessAccountId = params.get("bizId") || "";

            const promises = [];
            const skipHolidaysIfMatch = editForm.holiday === "Exclure";
            const skipHolidaysIfNoMatch = editForm.holiday === "Seulement";
            const unsetHoldays = !(skipHolidaysIfMatch || skipHolidaysIfNoMatch);

            //@ Prep effection exception object to save.
            const effExceptionToSave: NeedsConstraintsExceptionsUpdateModel = {
                holidayUnset: unsetHoldays,
                holiday: {
                    activeUnset: unsetHoldays ? void 0 : true,
                    active: unsetHoldays ? "false" : void 0,
                    actionIfMatchUnset: skipHolidaysIfMatch ? void 0 : true,
                    actionIfMatch: skipHolidaysIfMatch ? "skip" : void 0,
                    actionIfNoMatchUnset: skipHolidaysIfNoMatch ? void 0 : true,
                    actionIfNoMatch: skipHolidaysIfNoMatch ? "skip" : void 0,
                }
            };

            // A dedicated need key carries the holiday semantic itself; sending the exception
            // block would unset flags that may still exist on the stored data.
            const exceptionsToSave = editForm.needId ? undefined : effExceptionToSave;

            //@ Not sure how "Seulement" should behave
            const saveSpecificDay = true;
            const saveDaysOfWeek = true;
            const currentRole = rolesMap[editForm.rowKey] || ({
                label: "",
                roleId: "",
                rowKey: "",
                active: "true",
                apply: "true",
                dicOfDaysOfWeek: {},
                dicOfSpecificDays: {},
                name: ""
            } satisfies RoleInfo);
            const newDayOfWeekItemsToSave: UpdateModelForNeeds["dicOfRoleDayOfWeeksConstraints"] = {};
            const newSpecDayItemsToSave: UpdateModelForNeeds["dicOfRoleSpecDaysConstraints"] = {};
            let hasContentToSave = false;
            if (saveDaysOfWeek && editForm.dicOfDaysOfWeek) {

                const entries = Object.entries(editForm.dicOfDaysOfWeek);
                if (entries.length > 0) {

                    for (const [dayIndex, dayConfig] of entries) {
                        //@ EVENTUAL TODO: Use this "currentDayConfig" to eval effective changes to apply.
                        //var currentDayConfig = currentRole.dicOfDaysOfWeek ?  currentRole.dicOfDaysOfWeek[dayIndex]  : {}
                        hasContentToSave = true;
                        newDayOfWeekItemsToSave[dayIndex] = {
                            activeUnset: dayConfig.active !== "false",
                            active: dayConfig.active === "false" ? "false" : "true",
                            apply: dayConfig.apply === "false" ? "false" : "true",
                            min: dayConfig.min,
                            max: dayConfig.max,
                            start: dayConfig.start,
                            end: dayConfig.end,
                            equity: dayConfig.equity,
                            exceptions: exceptionsToSave
                        }
                    }

                }

            }

            if (saveSpecificDay && editForm.dicOfSpecificDays) {
                const entries = Object.entries(editForm.dicOfSpecificDays);
                const currentEntries = Object.entries(currentRole.dicOfSpecificDays || {});
                // var currentDayConfig = currentRole.dicOfSpecificDays ?  currentRole.dicOfSpecificDays[dateStr]  : {}

                for (const [dateStr, dayConfig] of entries) {

                    //currentDayConfig?.exceptions?.holiday?.active
                    hasContentToSave = true;
                    newSpecDayItemsToSave[dateStr] = {
                        activeUnset: dayConfig.active !== "false",
                        active: dayConfig.active === "false" ? "false" : "true",
                        equity: dayConfig.equity,
                        apply: dayConfig.apply === "false" ? "false" : "true",
                        min: dayConfig.min,
                        max: dayConfig.max,
                        start: dayConfig.start,
                        end: dayConfig.end,
                        exceptions: exceptionsToSave
                    }
                }
                //@ Set unactive if not contained anymore
                for (const [dateStr, dayConfig] of currentEntries) {
                    if (!newSpecDayItemsToSave[dateStr]) {
                        hasContentToSave = true;
                        newSpecDayItemsToSave[dateStr] = {
                            active: "false",
                        }
                    }
                }
            }


            if (hasContentToSave) {
                promises.push(updateExtendedWgData({
                    wgId,
                    businessAccountId,
                    needsUpdate: {
                        templateId: 'default',
                        roleId: editForm.roleId,
                        needId: editForm.needId,
                        apply: editForm.apply === "false" ? "false" : "true",
                        // roleDayOfWeekId: dayIndex,
                        dicOfRoleDayOfWeeksConstraints: newDayOfWeekItemsToSave,
                        dicOfRoleSpecDaysConstraints: newSpecDayItemsToSave
                    }
                }));
            }

            try {
                await Promise.all(promises);
                setRoles(prev => prev.map(role => role.rowKey === editForm.rowKey ? editForm : role));
                setEditingRowKey(null);
                setEditForm(null);
                //@ Force refetch/rerender UI
                setUpdateToken(updateToken + 1);
            } catch (err) {
                console.error("Failed to update role", err);
            }
        }
    };

    const handleConfigChange = (dayIndex: string, field: keyof SpecificNeedsConstraints | 'holiday', value: any, isException: boolean = false, dateStr: string = "") => {
        if (!editForm) return;

        if (field === 'holiday') {
            setEditForm({
                ...editForm,
                holiday: value as HolidayInclusion
            });

            return;
        }

        const defaultConfig: SpecificNeedsConstraints = {
            active: "true",
            apply: "true",
            start: 0,
            end: 0,
            min: 0,
            max: 0
        };

        if (isException && dateStr) {
            const newExceptions = { ...(editForm.dicOfSpecificDays || {}) };

            if (field === 'active' && value === 'TOGGLE_EXCEPTION') {
                if (newExceptions[dateStr] && newExceptions[dateStr].active !== "false") {
                    delete newExceptions[dateStr];
                } else {
                    const currentDayConfig = (editForm.dicOfDaysOfWeek?.[dayIndex]) || defaultConfig;
                    newExceptions[dateStr] = { ...currentDayConfig };
                    newExceptions[dateStr].active = "true";
                }
            } else {
                if (!(newExceptions[dateStr] && newExceptions[dateStr].active !== "false")) {
                    const currentDayConfig = (editForm.dicOfDaysOfWeek?.[dayIndex]) || defaultConfig;
                    newExceptions[dateStr] = { ...currentDayConfig };
                }

                newExceptions[dateStr] = {
                    ...newExceptions[dateStr],
                    [field]: value
                };
            }

            setEditForm({
                ...editForm,
                dicOfSpecificDays: newExceptions
            });

        } else {
            const currentConfig = (editForm.dicOfDaysOfWeek?.[dayIndex]) || defaultConfig;
            setEditForm({
                ...editForm,
                dicOfDaysOfWeek: {
                    ...editForm.dicOfDaysOfWeek,
                    [dayIndex]: {
                        ...currentConfig,
                        [field]: value,
                        "active": "true"
                    }
                }
            });
        }
    };

    const handlePrevWeek = () => {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(newDate.getDate() - 7);
        setCurrentWeekStart(newDate);
    };

    const handleNextWeek = () => {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(newDate.getDate() + 7);
        setCurrentWeekStart(newDate);
    };

    const handleRoleApplyToggle = async (role: RoleInfo | null, apply: string) => {
        if (!role) {
            return;
        }

        if (editForm && editForm.rowKey === role.rowKey) {
            setEditForm({
                ...editForm,
                apply
            });
            return;
        }

        try {
            const params = new URLSearchParams(window.location.search);
            const wgId = params.get("wgId") || "";
            const businessAccountId = params.get("bizId") || "";

            await updateExtendedWgData({
                wgId,
                businessAccountId,
                needsUpdate: {
                    templateId: 'default',
                    roleId: role.roleId,
                    needId: role.needId,
                    apply
                }
            });

            setRoles(prev => prev.map(r => r.rowKey === role.rowKey ? {
                ...r,
                apply
            } : r));
        } catch (err) {
            console.error("Failed to toggle role active state", err);
        }
    };

    return (
        <div className="w-full py-4">
            <div className="flex justify-between items-center mb-4">
                <Button
                    onClick={handlePrevWeek}
                    type="primary"
                    label="Semaine précédente"
                    icon={<ChevronLeft size={20}/>}
                    rounded
                    className="flex items-center gap-2"
                    style={{ backgroundColor: 'dodgerblue' }}
                />
                <div className="font-bold text-lg">
                    Semaine du {currentWeekStart.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                })}
                </div>
                <Button
                    onClick={handleNextWeek}
                    type="primary"
                    label="Semaine suivante"
                    icon={<ChevronRight size={20}/>}
                    iconPosition="right"
                    rounded
                    className="flex items-center gap-2"
                    style={{ backgroundColor: 'dodgerblue' }}
                />
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-300">
                <table className="w-full border-collapse text-center text-sm">
                    <RolesTableHeader
                        days={weekDays}
                        showNeedColumn={useNewTaskNeedsUI}
                        roleFilter={
                            <TimelineFilterOverlay
                                isOpen={roleFilterOpen}
                                onOpenChange={setRoleFilterOpen}
                                items={roleFilterItems}
                                selectedItems={selectedRoleIds}
                                onSelectionChange={setSelectedRoleIds}
                                title="Sélection des rôles"
                            >
                                <FilterButton
                                    count={roleFilterCount}
                                    className={`${roleFilterOpen ? "bg-gray-200" : "bg-white"} text-gray-700 rounded-md`}
                                />
                            </TimelineFilterOverlay>
                        }
                    />
                    <tbody>
                    {visibleRoles.map((role) => (
                        isFoldedHolidayRow(role) ? (
                            <HolidayIndicatorRow
                                key={role.rowKey}
                                label={NEED_KEY_LABELS[NEED_KEYS[1]] ?? NEED_KEYS[1]}
                                days={weekDays}
                                expanded={false}
                                onToggle={() => toggleNeedRows(role.roleId)}
                            />
                        ) : (
                        <RolesTableRow
                            key={role.rowKey}
                            role={role}
                            isEditing={editingRowKey === role.rowKey}
                            editForm={editForm}
                            days={weekDays}
                            onEdit={handleEdit}
                            onSave={handleSave}
                            onCancel={handleCancel}
                            onConfigChange={handleConfigChange}
                            onRoleApplyToggle={handleRoleApplyToggle}
                            onRoleLabelClick={(r) => setCalendarRowKey(r.rowKey)}
                            needLabel={role.needId ? NEED_KEY_LABELS[role.needId] ?? role.needId : undefined}
                            showRoleCell={!role.needId || role.needId === NEED_KEYS[0]}
                            roleCellRowSpan={role.needId ? NEED_KEYS.length : undefined}
                            needRowsToggle={role.needId === NEED_KEYS[1] ? {
                                expanded: expandedNeedRoleIds.has(role.roleId),
                                onToggle: () => toggleNeedRows(role.roleId),
                                disabled: editingRowKey === role.rowKey
                            } : undefined}
                            markHolidays={role.needId === NEED_KEYS[1]}
                        />
                        )
                    ))}
                    </tbody>
                </table>
            </div>

            <NeedsExceptionCalendarModal
                open={!!calendarRole}
                onClose={() => setCalendarRowKey(null)}
                role={calendarRole}
                startDate={periodStartDate}
                endDate={periodEndDate}
                holidays={schedule?.holidays}
                onPrev={() => {
                    const prev = calendarRoles[calendarRoleIndex - 1];
                    if (prev) setCalendarRowKey(prev.rowKey);
                }}
                onNext={() => {
                    const next = calendarRoles[calendarRoleIndex + 1];
                    if (next) setCalendarRowKey(next.rowKey);
                }}
                hasPrev={calendarRoleIndex > 0}
                hasNext={calendarRoleIndex >= 0 && calendarRoleIndex < calendarRoles.length - 1}
                onSaved={() => setUpdateToken(t => t + 1)}
            />
        </div>
    );
}
